/**
 * Nightwave — facets.js
 * AJAX filtering and sorting for collection and search pages.
 * Fetches the section with the new query (Section Rendering API), swaps the
 * results grid and the filters, and keeps the URL in sync.
 */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const cache = new Map();
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facets-form]');
      this.overlay = this.querySelector('.facets__overlay');
      this.root = this.closest('section') || document;
      if (!this.form) return;

      this.debouncedSubmit = theme.utils.debounce(() => this.submit(), 700);

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
        if (event.submitter && event.submitter.classList.contains('facets__apply')) this.closePanel();
      });

      this.form.addEventListener('change', (event) => {
        if (event.target.closest('[data-price-range]')) this.debouncedSubmit();
        else if (event.target.matches('input, select')) this.submit();
      });

      this.form.addEventListener('input', (event) => {
        if (event.target.closest('[data-price-range]')) this.debouncedSubmit();
      });

      this.onRootClick = (event) => {
        const link = event.target.closest('[data-facet-link]');
        if (link) {
          event.preventDefault();
          this.fetchSection(new URL(link.href, window.location.href).searchParams.toString());
          return;
        }
        if (event.target.closest('[data-facets-open]')) this.openPanel();
        else if (event.target.closest('[data-facets-close]')) this.closePanel();
      };
      this.root.addEventListener('click', this.onRootClick);

      // Only one dropdown open at a time on desktop
      this.addEventListener(
        'toggle',
        (event) => {
          if (!event.target.matches('[data-facet]') || !event.target.open || !desktopQuery.matches) return;
          this.querySelectorAll('[data-facet][open]').forEach((details) => {
            if (details !== event.target) details.open = false;
          });
        },
        true
      );

      this.onOutsideClick = (event) => {
        if (!desktopQuery.matches || this.contains(event.target)) return;
        this.querySelectorAll('[data-facet][open]').forEach((details) => {
          details.open = false;
        });
      };
      document.addEventListener('click', this.onOutsideClick);

      this.onKeyUp = (event) => {
        if (event.key !== 'Escape') return;
        const openDetails = this.querySelector('[data-facet][open]');
        if (desktopQuery.matches && openDetails) {
          openDetails.open = false;
          openDetails.querySelector('summary')?.focus();
        } else {
          this.closePanel();
        }
      };
      this.addEventListener('keyup', this.onKeyUp);

      this.onPopState = () => this.fetchSection(window.location.search.slice(1), false);
      window.addEventListener('popstate', this.onPopState);

      this.sortNumericValues();
    }

    disconnectedCallback() {
      this.root?.removeEventListener('click', this.onRootClick);
      document.removeEventListener('click', this.onOutsideClick);
      window.removeEventListener('popstate', this.onPopState);
    }

    buildQuery() {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form).entries()) {
        if (String(value).trim() !== '') params.append(key, value);
      }
      return params.toString();
    }

    submit() {
      this.fetchSection(this.buildQuery());
    }

    async fetchSection(query, updateHistory = true) {
      const grid = document.getElementById(`ProductGridContainer-${this.sectionId}`);
      grid?.classList.add('is-loading');

      const url = `${window.location.pathname}?${query ? `${query}&` : ''}section_id=${this.sectionId}`;
      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        let html = cache.get(url);
        if (!html) {
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(String(response.status));
          html = await response.text();
          cache.set(url, html);
        }
        this.render(html);
        if (updateHistory) {
          window.history.pushState({ facets: true }, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
      }
    }

    render(html) {
      const doc = theme.utils.parseHTML(html);

      const freshGrid = doc.getElementById(`ProductGridContainer-${this.sectionId}`);
      const grid = document.getElementById(`ProductGridContainer-${this.sectionId}`);
      if (freshGrid && grid) {
        grid.innerHTML = freshGrid.innerHTML;
        grid.classList.remove('is-loading');
      }

      const freshForm = doc.querySelector(`facet-filters[data-section-id="${this.sectionId}"] [data-facets-form]`);
      if (freshForm) {
        const openLabels = [...this.querySelectorAll('[data-facet][open] .facet__label')].map((label) => label.textContent);
        const panelWasOpen = Boolean(this.querySelector('[data-facets-panel].is-open'));
        const listScroll = this.querySelector('.facets__list')?.scrollTop || 0;
        const activeId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;

        this.form.innerHTML = freshForm.innerHTML;

        this.querySelectorAll('[data-facet]').forEach((details) => {
          const label = details.querySelector('.facet__label');
          if (label && openLabels.includes(label.textContent)) details.open = true;
        });

        if (panelWasOpen) {
          this.querySelector('[data-facets-panel]')?.classList.add('is-open');
          const list = this.querySelector('.facets__list');
          if (list) list.scrollTop = listScroll;
        }

        if (activeId) {
          const element = document.getElementById(activeId);
          if (element) {
            element.focus({ preventScroll: true });
            if (element.type === 'number') {
              try {
                element.setSelectionRange(element.value.length, element.value.length);
              } catch (error) {
                /* number inputs may not support selection */
              }
            }
          }
        }

        this.sortNumericValues();
      }

      theme.events.emit('tracks:changed');
    }

    openPanel() {
      const panel = this.querySelector('[data-facets-panel]');
      if (!panel) return;
      this.lastFocus = document.activeElement;
      panel.classList.add('is-open');
      if (this.overlay) this.overlay.hidden = false;
      this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      panel.querySelector('[data-facets-close]')?.focus();
    }

    closePanel() {
      const panel = this.querySelector('[data-facets-panel]');
      if (!panel || !panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      if (this.overlay) this.overlay.hidden = true;
      this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
      else this.querySelector('[data-facets-open]')?.focus();
    }

    /** Sorts numeric filter values (e.g. BPM) in ascending order. */
    sortNumericValues() {
      this.querySelectorAll('[data-facet-values]').forEach((list) => {
        const items = [...list.children];
        const valueOf = (item) => item.querySelector('[data-label]')?.dataset.label.trim() || '';
        if (items.length < 2 || !items.every((item) => /^\d+([.,]\d+)?$/.test(valueOf(item)))) return;
        items
          .sort((a, b) => parseFloat(valueOf(a).replace(',', '.')) - parseFloat(valueOf(b).replace(',', '.')))
          .forEach((item) => list.appendChild(item));
      });
    }
  }

  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
})();
